/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useRef, useState } from 'react';
import { Ionicons } from '@expo/vector-icons';
import {
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
  type ScrollView,
} from 'react-native';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import { CONTENT_WIDE_BREAKPOINT } from '../../shared/layout/breakpoints';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import {
  STOCK_MOVEMENT_REASONS,
  getStockLotExpirySplit,
  listVariantStockCells,
  type StockMovementReasonKey,
  type StockRow,
  type VariantStockCellRow,
} from '../../inventory/stock/public-api';
import { OperationalChipSelectRow } from '../../shared/filters/OperationalChipSelectRow';
import { OperationalFilterPickerRow } from '../../shared/filters/OperationalFilterPrimitives';
import { ModalNestedPicker } from '../../shared/modals/ModalNestedPicker';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { ModalShell } from '../../shared/modals/ModalShell'
import { ModalFormField } from '../../shared/modals/ModalFormField'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { parseDecimalInput } from '../../shared/numeric/localeNumeric';
import {
  currencyFormatter,
  formatStockQuantity,
  normalizeStockUnitOfMeasure,
  stockQuantityAllowsFraction,
} from '../application/inventoryUtils';
import { formatUnitLabel } from '../../catalog/domain-api';
import {
  getInventoryStockStatusLabel,
  getInventoryStockStatusTone,
} from '../application/inventoryStockStatusPresentation';

type BranchLike = { id: string; name: string };

type Props = {
  adjustModalVisible: boolean;
  adjustStockRow: StockRow | null;
  adjustDelta: string;
  adjustReason: StockMovementReasonKey;
  adjustSaving: boolean;
  onAdjustDeltaChange: (value: string) => void;
  onAdjustReasonChange: (value: StockMovementReasonKey) => void;
  onCloseAdjust: () => void;
  onApplyAdjustment: () => void;
  onAdjustStockRowChange: (row: StockRow) => void;
  transferModalVisible: boolean;
  selectedStock: StockRow | null;
  destinationBranches: BranchLike[];
  destinationBranchId: string | null;
  transferQuantity: string;
  transferWaitForReceive: boolean;
  isSubmittingTransfer: boolean;
  transferError: string | null;
  onDestinationBranchChange: (value: string) => void;
  onTransferQuantityChange: (value: string) => void;
  onToggleTransferWaitForReceive: () => void;
  onCloseTransfer: () => void;
  onConfirmTransfer: () => void;
  onTransferStockRowChange: (row: StockRow) => void;
  /** #709: si la capability product_variants está activa, muestra selectores de variante. */
  variantsEnabled?: boolean;
};

const getStatusPill = (stock: StockRow | null) => {
  if (!stock) return null;
  const label = getInventoryStockStatusLabel(stock);
  if (label === 'OK') return null;
  const tone = getInventoryStockStatusTone(stock);
  return { label, tone };
};

export const InventoryStockActionModals = ({
  adjustModalVisible,
  adjustStockRow,
  adjustDelta,
  adjustReason,
  adjustSaving,
  onAdjustDeltaChange,
  onAdjustReasonChange,
  onCloseAdjust,
  onApplyAdjustment,
  onAdjustStockRowChange,
  transferModalVisible,
  selectedStock,
  destinationBranches,
  destinationBranchId,
  transferQuantity,
  transferWaitForReceive,
  isSubmittingTransfer,
  transferError,
  onDestinationBranchChange,
  onTransferQuantityChange,
  onToggleTransferWaitForReceive,
  onCloseTransfer,
  onConfirmTransfer,
  onTransferStockRowChange,
  variantsEnabled = false,
}: Props) => {
  const adjustScrollRef = useRef<ScrollView>(null);
  const adjustContentRef = useRef<View>(null);

  const transferScrollRef = useRef<ScrollView>(null);
  const transferContentRef = useRef<View>(null);

  // Variant cells for adjust modal
  const [adjustVariantCells, setAdjustVariantCells] = useState<VariantStockCellRow[]>([]);
  const [adjustSelectedVariantId, setAdjustSelectedVariantId] = useState<string | null>(null);

  // Variant cells for transfer modal
  const [transferVariantCells, setTransferVariantCells] = useState<VariantStockCellRow[]>([]);
  const [transferSelectedVariantId, setTransferSelectedVariantId] = useState<string | null>(null);

  // Destino vía picker con búsqueda (muchas sucursales) y modo explícito:
  // `transferWaitForReceive=false` en el store NO significa "Directa" elegida —
  // el usuario tiene que tocar un modo antes de poder confirmar.
  const [branchPickerOpen, setBranchPickerOpen] = useState(false);
  const [transferModeChosen, setTransferModeChosen] = useState(false);
  const { confirm: confirmDiscardTransfer, ConfirmModal: TransferDiscardConfirm } =
    useOperationalConfirm();

  // Load variant cells when adjust modal opens
  // #709: solo cargar si la capability product_variants está activa.
  useEffect(() => {
    if (!adjustModalVisible || !adjustStockRow || !variantsEnabled) {
      setAdjustVariantCells([]);
      setAdjustSelectedVariantId(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const cells = await listVariantStockCells(adjustStockRow.branchId, adjustStockRow.productId);
        if (!cancelled) {
          setAdjustVariantCells(cells);
          setAdjustSelectedVariantId(null);
        }
      } catch {
        if (!cancelled) {
          setAdjustVariantCells([]);
          setAdjustSelectedVariantId(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [adjustModalVisible, adjustStockRow, variantsEnabled]);

  // Load variant cells when transfer modal opens
  // #709: solo cargar si la capability product_variants está activa.
  useEffect(() => {
    if (!transferModalVisible || !selectedStock || !variantsEnabled) {
      setTransferVariantCells([]);
      setTransferSelectedVariantId(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const cells = await listVariantStockCells(selectedStock.branchId, selectedStock.productId);
        if (!cancelled) {
          setTransferVariantCells(cells);
          setTransferSelectedVariantId(null);
        }
      } catch {
        if (!cancelled) {
          setTransferVariantCells([]);
          setTransferSelectedVariantId(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [transferModalVisible, selectedStock, variantsEnabled]);

  // Adjust computed values
  const adjustStatusPill = getStatusPill(adjustStockRow);
  const adjustUom = normalizeStockUnitOfMeasure(adjustStockRow?.unitOfMeasure);
  const adjustAllowsFraction = stockQuantityAllowsFraction(adjustUom);
  const adjustUnitLabel = formatUnitLabel(adjustUom);
  const adjustDeltaPreview =
    parseDecimalInput(adjustDelta, {
      integerOnly: !adjustAllowsFraction,
      allowNegative: true,
    }) ?? 0;
  const adjustCurrentQty = adjustStockRow?.quantity ?? 0;
  const adjustResultingQty = adjustCurrentQty + adjustDeltaPreview;
  const adjustAvgCost = adjustStockRow?.avgCost ?? 0;
  const adjustValueDiff = adjustDeltaPreview * adjustAvgCost;
  // #1497: los ajustes negativos consumen lotes vencidos primero (expiredFirst).
  // Avisar cuántas vencidas va a tocar este ajuste — informativo, no bloquea.
  const adjustLotSplit =
    adjustModalVisible && adjustStockRow && adjustStockRow.variantId == null
      ? getStockLotExpirySplit(adjustStockRow.productId, adjustStockRow.branchId)
      : null;
  const adjustExpiredQty =
    adjustDeltaPreview < 0
      ? Math.min(adjustLotSplit?.expiredQuantity ?? 0, Math.abs(adjustDeltaPreview))
      : 0;
  const adjustMetaParts: string[] = [];
  if (adjustStockRow) {
    adjustMetaParts.push(`Mínimo ${formatStockQuantity(adjustStockRow.minQuantity, adjustUom)}`);
    adjustMetaParts.push(`Costo promedio ${currencyFormatter.format(adjustAvgCost)}`);
  }

  // Reset del modo/picker al abrir el modal o cambiar de producto
  // (cambiar de variante conserva el modo elegido).
  const selectedStockProductId = selectedStock?.productId ?? null;
  useEffect(() => {
    setTransferModeChosen(false);
    setBranchPickerOpen(false);
  }, [transferModalVisible, selectedStockProductId]);

  // Transfer computed values
  const transferStatusPill = getStatusPill(selectedStock);
  const transferUom = normalizeStockUnitOfMeasure(selectedStock?.unitOfMeasure);
  const transferAllowsFraction = stockQuantityAllowsFraction(transferUom);
  const transferUnitLabel = formatUnitLabel(transferUom);
  const transferQtyParsed = parseDecimalInput(transferQuantity, { min: 0 });
  const transferQtyValid = transferQtyParsed != null && transferQtyParsed > 0;
  const transferCurrentQty = selectedStock?.quantity ?? 0;
  // Preview honesto: muestra lo que efectivamente se movería (clamp al
  // disponible), nunca un número distinto del que se va a aplicar.
  const transferQtyEffective = Math.min(transferQtyParsed ?? 0, transferCurrentQty);
  const transferOriginAfter = transferCurrentQty - transferQtyEffective;
  const transferExceedsStock =
    transferQtyParsed != null && transferQtyParsed > transferCurrentQty;
  const transferDestBranchName =
    destinationBranches.find((b) => b.id === destinationBranchId)?.name ?? null;
  // Confirmar solo con destino + modo + cantidad explícitos y válidos.
  const canSubmitTransfer =
    Boolean(destinationBranchId) && transferModeChosen && transferQtyValid;
  const isTransferDirty =
    Boolean(destinationBranchId) ||
    transferModeChosen ||
    transferSelectedVariantId != null ||
    transferQuantity.trim() !== '1';

  const requestCloseTransfer = () => {
    if (isSubmittingTransfer) return;
    if (!isTransferDirty) {
      onCloseTransfer();
      return;
    }
    void confirmDiscardTransfer({
      title: 'Descartar transferencia',
      message: 'Cargaste datos de la transferencia. Si cerrás, se pierden.',
      confirmLabel: 'Descartar',
      destructive: true,
      iconName: 'trash-outline',
      presentation: 'overlay',
    }).then((confirmed) => {
      if (confirmed) onCloseTransfer();
    });
  };

  const { width: winW } = useWindowDimensions();
  const isMobile = winW < CONTENT_WIDE_BREAKPOINT;

  const footerActionsStyle = isMobile ? styles.footerActionsStack : styles.footerActions;
  const btnSecondaryStyle = isMobile ? [styles.mobileBtnSecondary] : [posUi.secondaryButton, styles.btnSecondary];
  const btnPrimaryStyle = isMobile ? [styles.mobileBtnPrimary] : [posUi.primaryButton, styles.btnPrimary];
  const btnSecondaryTextStyle = isMobile ? styles.mobileBtnSecondaryText : posUi.secondaryButtonText;
  const btnPrimaryTextStyle = isMobile ? styles.mobileBtnPrimaryText : posUi.primaryButtonText;

  return (
    <>
      {/* ===== Adjust Modal ===== */}
      <ModalShell
        visible={adjustModalVisible}
        onClose={onCloseAdjust}
        appearance="ticket"
        kicker="Stock"
        title="Ajustar stock"
        subtitle={adjustStockRow?.productName}
        widthVariant="standard"
        scrollRef={adjustScrollRef}
        contentRef={adjustContentRef}
        scrollBottomInset={spacing.sm}
        scrollPadding={spacing.md}
        scrollContentStyle={modalExperience.formStack}
        footer={
          <View style={footerActionsStyle}>
            <TouchableOpacity style={btnSecondaryStyle} onPress={onCloseAdjust} accessibilityRole="button" accessibilityLabel="Cancelar ajuste">
              <Text style={btnSecondaryTextStyle}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[btnPrimaryStyle, adjustSaving && posUi.disabledCard]}
              onPress={onApplyAdjustment}
              disabled={adjustSaving}
              accessibilityRole="button"
              accessibilityLabel={adjustSaving ? 'Guardando ajuste' : 'Aplicar ajuste'}
            >
              <Text style={btnPrimaryTextStyle}>
                {adjustSaving ? 'Guardando…' : 'Aplicar ajuste'}
              </Text>
            </TouchableOpacity>
          </View>
        }
      >
        {/* Hero */}
        <View style={modalExperience.detailMetricHero}>
          <View style={modalExperience.detailMetricMain}>
            <Text style={modalExperience.detailMetricLabel}>Stock actual</Text>
            <Text style={modalExperience.detailMetricValue}>
              {formatStockQuantity(adjustCurrentQty, adjustUom)}
            </Text>
            <Text style={modalExperience.detailMetricMeta}>{adjustMetaParts.join(' · ')}</Text>
          </View>
          {adjustStatusPill ? (
            <View
              style={[
                modalExperience.statusPill,
                adjustStatusPill.tone === 'critical'
                  ? modalExperience.statusPillCritical
                  : modalExperience.statusPillWarning,
              ]}
            >
              <Text
                style={
                  adjustStatusPill.tone === 'critical'
                    ? modalExperience.statusPillTextCritical
                    : modalExperience.statusPillTextWarning
                }
              >
                {adjustStatusPill.label}
              </Text>
            </View>
          ) : null}
        </View>

        {adjustVariantCells.length > 0 ? (
          <ModalFormField label="Variante">
            <OperationalChipSelectRow
              ticketTone
              value={adjustSelectedVariantId ?? ''}
              onChange={(v) => {
                setAdjustSelectedVariantId(v === '' ? null : v);
                const cell = v === '' ? null : adjustVariantCells.find((c) => c.variantId === v);
                if (cell && adjustStockRow) {
                  onAdjustStockRowChange({
                    ...adjustStockRow,
                    id: cell.stockId ?? cell.variantId,
                    quantity: cell.quantity,
                    variantId: cell.variantId,
                  });
                } else if (v === '' && adjustStockRow) {
                  onAdjustStockRowChange({
                    ...adjustStockRow,
                    variantId: null,
                  });
                }
              }}
              options={[
                { value: '', label: 'Sin variante (parent)' },
                ...adjustVariantCells.map((c) => ({ value: c.variantId, label: c.label })),
              ]}
            />
          </ModalFormField>
        ) : null}

        {/* Form */}
        <View style={modalExperience.formPanel}>
          <Text style={modalExperience.detailBlockTitle}>Ajuste manual</Text>
          <ModalFormField
            label={
              adjustAllowsFraction
                ? `Cantidad en ${adjustUnitLabel} (positivo suma, negativo resta)`
                : 'Cantidad (positivo suma, negativo resta)'
            }
          >
            <NumericTextInput
              kind={adjustAllowsFraction ? 'signedDecimal' : 'signedInteger'}
              style={modalExperience.formInput}
              value={adjustDelta}
              onChangeText={onAdjustDeltaChange}
              placeholder={adjustAllowsFraction ? 'Ej: -0,5' : 'Ej: -3'}
              placeholderTextColor={ticketColors.ink3}
            />
          </ModalFormField>
          <ModalFormField label="Motivo">
            <OperationalChipSelectRow
              ticketTone
              value={adjustReason}
              onChange={onAdjustReasonChange}
              options={(['shrinkage', 'breakage', 'expiry_writeoff', 'return', 'manual_adjustment'] as const).map((key) => ({
                value: key,
                label: STOCK_MOVEMENT_REASONS[key],
              }))}
            />
          </ModalFormField>
          {adjustExpiredQty > 0 ? (
            <View style={styles.expiredWarn} accessibilityLiveRegion="polite">
              <Ionicons name="information-circle-outline" size={16} color={ticketColors.warn} />
              <Text style={styles.expiredWarnText}>
                Este ajuste descuenta primero {formatStockQuantity(adjustExpiredQty, adjustUom)}{' '}
                vencida{adjustExpiredQty === 1 ? '' : 's'}.
              </Text>
            </View>
          ) : null}
        </View>

        {/* Preview */}
        {adjustDelta !== '' ? (
          <View style={modalExperience.infoPanel}>
            <Text style={modalExperience.detailBlockTitle}>Resultado</Text>
            <View style={modalExperience.detailKvRow}>
              <Text style={modalExperience.detailMetaLabel}>Stock resultante</Text>
              <Text style={modalExperience.detailKvValueStrong}>
                {formatStockQuantity(adjustResultingQty, adjustUom)}
              </Text>
            </View>
            <View style={modalExperience.detailKvRow}>
              <Text style={modalExperience.detailMetaLabel}>Diferencia valor</Text>
              <Text
                style={[
                  modalExperience.detailKvValueStrong,
                  adjustValueDiff < 0 ? modalExperience.detailMetaValueDanger : null,
                ]}
              >
                {adjustValueDiff < 0 ? '−' : '+'}
                {currencyFormatter.format(Math.abs(adjustValueDiff))}
              </Text>
            </View>
          </View>
        ) : null}
      </ModalShell>

      {/* ===== Transfer Modal ===== */}
      <ModalShell
        visible={transferModalVisible}
        onClose={requestCloseTransfer}
        onRequestClose={requestCloseTransfer}
        appearance="ticket"
        kicker="Stock"
        title="Transferir stock"
        subtitle={selectedStock?.productName}
        widthVariant="standard"
        scrollRef={transferScrollRef}
        contentRef={transferContentRef}
        scrollBottomInset={spacing.sm}
        scrollPadding={spacing.md}
        scrollContentStyle={modalExperience.formStack}
        overlay={TransferDiscardConfirm}
        footer={
          <View style={footerActionsStyle}>
            <TouchableOpacity style={btnSecondaryStyle} onPress={requestCloseTransfer} accessibilityRole="button" accessibilityLabel="Cancelar transferencia">
              <Text style={btnSecondaryTextStyle}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[btnPrimaryStyle, (isSubmittingTransfer || !canSubmitTransfer) && posUi.disabledCard]}
              onPress={onConfirmTransfer}
              disabled={isSubmittingTransfer || !canSubmitTransfer}
              accessibilityRole="button"
              accessibilityLabel={isSubmittingTransfer ? 'Transfiriendo stock' : 'Confirmar transferencia'}
            >
              <Text style={btnPrimaryTextStyle}>
                {isSubmittingTransfer ? 'Transfiriendo…' : 'Confirmar transferencia'}
              </Text>
            </TouchableOpacity>
          </View>
        }
      >
        {/* Hero */}
        <View style={modalExperience.detailMetricHero}>
          <View style={modalExperience.detailMetricMain}>
            <Text style={modalExperience.detailMetricLabel}>Stock disponible</Text>
            <Text style={modalExperience.detailMetricValue}>
              {formatStockQuantity(transferCurrentQty, transferUom)}
            </Text>
            <Text style={modalExperience.detailMetricMeta}>En esta sucursal</Text>
          </View>
          {transferStatusPill ? (
            <View
              style={[
                modalExperience.statusPill,
                transferStatusPill.tone === 'critical'
                  ? modalExperience.statusPillCritical
                  : modalExperience.statusPillWarning,
              ]}
            >
              <Text
                style={
                  transferStatusPill.tone === 'critical'
                    ? modalExperience.statusPillTextCritical
                    : modalExperience.statusPillTextWarning
                }
              >
                {transferStatusPill.label}
              </Text>
            </View>
          ) : null}
        </View>

        {transferVariantCells.length > 0 ? (
          <ModalFormField label="Variante">
            <OperationalChipSelectRow
              ticketTone
              value={transferSelectedVariantId ?? ''}
              onChange={(v) => {
                setTransferSelectedVariantId(v === '' ? null : v);
                const cell = v === '' ? null : transferVariantCells.find((c) => c.variantId === v);
                if (cell && selectedStock) {
                  onTransferStockRowChange({
                    ...selectedStock,
                    id: cell.stockId ?? cell.variantId,
                    quantity: cell.quantity,
                    variantId: cell.variantId,
                  });
                } else if (v === '' && selectedStock) {
                  onTransferStockRowChange({
                    ...selectedStock,
                    variantId: null,
                  });
                }
              }}
              options={[
                { value: '', label: 'Sin variante (parent)' },
                ...transferVariantCells.map((c) => ({ value: c.variantId, label: c.label })),
              ]}
            />
          </ModalFormField>
        ) : null}

        <ModalFormField label={transferAllowsFraction ? `Cantidad (${transferUnitLabel})` : 'Cantidad'}>
          <NumericTextInput
            kind={transferAllowsFraction ? 'decimalMoney' : 'integer'}
            style={modalExperience.formInput}
            value={transferQuantity}
            onChangeText={onTransferQuantityChange}
            placeholder={transferAllowsFraction ? 'Ej: 1,5' : 'Ej: 5'}
            placeholderTextColor={ticketColors.ink3}
          />
        </ModalFormField>
        {/* Destino: picker con búsqueda (las orgs pueden tener muchas sucursales
            y nombres largos). Sin preselección — hay que elegir. */}
        <OperationalFilterPickerRow
          layout="stack"
          label="Sucursal destino"
          valueLabel={transferDestBranchName ?? 'Elegir…'}
          active={Boolean(destinationBranchId)}
          onPress={() => setBranchPickerOpen(true)}
          accessibilityLabel="Elegir sucursal destino"
        />

        {/* Modo explícito: sin elegir no se puede confirmar. Reemplaza al
            checkbox 22px (touch target chico) — los chips ticket son ≥44px. */}
        <ModalFormField label="Modo de envío">
          <OperationalChipSelectRow
            ticketTone
            value={transferModeChosen ? (transferWaitForReceive ? 'wait' : 'direct') : ''}
            onChange={(v) => {
              setTransferModeChosen(true);
              const wantsWait = v === 'wait';
              if (wantsWait !== transferWaitForReceive) onToggleTransferWaitForReceive();
            }}
            options={[
              { value: 'direct', label: 'Directa' },
              { value: 'wait', label: 'La reciben en destino' },
            ]}
          />
          {transferModeChosen ? (
            <Text style={modalExperience.formSectionHelper}>
              {transferWaitForReceive
                ? 'Queda pendiente hasta que la reciban en destino.'
                : 'El stock se mueve apenas confirmás.'}
            </Text>
          ) : null}
        </ModalFormField>

        {/* Preview honesto: solo con cantidad válida. Si supera lo disponible
            se avisa — nunca mostrar un número distinto del que se va a mover. */}
        {transferQtyValid ? (
          <View style={modalExperience.infoPanel}>
            <Text style={modalExperience.detailBlockTitle}>Resumen</Text>
            <View style={modalExperience.detailKvRow}>
              <Text style={modalExperience.detailMetaLabel}>Origen</Text>
              <Text style={modalExperience.detailKvValueStrong}>
                {formatStockQuantity(transferCurrentQty, transferUom)} →{' '}
                {formatStockQuantity(transferOriginAfter, transferUom)}
              </Text>
            </View>
            <View style={modalExperience.detailKvRow}>
              <Text style={modalExperience.detailMetaLabel}>Destino</Text>
              <Text style={modalExperience.detailKvValueStrong} numberOfLines={1}>
                {transferDestBranchName ?? 'Sin elegir'}
                {transferExceedsStock
                  ? ''
                  : ` (+${formatStockQuantity(transferQtyEffective, transferUom)})`}
              </Text>
            </View>
            {transferExceedsStock ? (
              <View style={modalExperience.detailKvRow}>
                <Text style={modalExperience.detailMetaLabel}>Atención</Text>
                <Text
                  style={[
                    modalExperience.detailKvValueStrong,
                    modalExperience.detailMetaValueDanger,
                  ]}
                >
                  Ingresaste {formatStockQuantity(transferQtyParsed ?? 0, transferUom)} pero hay{' '}
                  {formatStockQuantity(transferCurrentQty, transferUom)} disponibles.
                </Text>
              </View>
            ) : null}
            <View style={modalExperience.detailKvRow}>
              <Text style={modalExperience.detailMetaLabel}>Estado</Text>
              <Text style={modalExperience.detailKvValueStrong}>
                {transferModeChosen
                  ? transferWaitForReceive
                    ? 'Pendiente de recepción'
                    : 'Directa'
                  : 'Sin elegir'}
              </Text>
            </View>
          </View>
        ) : null}

        {transferError ? (
          <View style={modalExperience.errorBanner}>
            <Ionicons name="alert-circle" size={18} color={ticketColors.bad} />
            <Text style={modalExperience.errorBannerText}>{transferError}</Text>
          </View>
        ) : null}
      </ModalShell>

      {/* Picker de sucursal destino (sheet con búsqueda, encima del modal). */}
      <ModalNestedPicker
        visible={branchPickerOpen}
        title="Sucursal destino"
        options={destinationBranches.map((branch) => ({
          value: branch.id,
          label: branch.name,
        }))}
        selectedValue={destinationBranchId ?? ''}
        onSelect={(id) => {
          if (id) onDestinationBranchChange(id);
          setBranchPickerOpen(false);
        }}
        onClose={() => setBranchPickerOpen(false)}
      />
    </>
  );
};

const styles = StyleSheet.create({
  footerActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    width: '100%',
  },
  footerActionsStack: {
    flexDirection: 'column',
    gap: spacing.sm,
    width: '100%',
  },
  btnSecondary: {
    flex: 1,
    paddingHorizontal: spacing.md,
    minHeight: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  btnPrimary: {
    flex: 1,
    minHeight: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  mobileBtnSecondary: {
    minHeight: 42,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    width: '100%',
  },
  mobileBtnSecondaryText: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  mobileBtnPrimary: {
    minHeight: 42,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    width: '100%',
  },
  mobileBtnPrimaryText: {
    fontSize: 14,
    fontWeight: '800',
    color: ticketColors.goldInk,
  },
  expiredWarn: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.xs,
    borderWidth: 1,
    borderColor: ticketColors.warnBorder,
    backgroundColor: ticketColors.warnBg,
    borderRadius: 10,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    marginTop: spacing.xs,
  },
  expiredWarnText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '600',
    color: ticketColors.warn,
  },
});
